import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmationService } from 'primeng/api';
import { DialogService } from 'primeng/dynamicdialog';
import { Subject, Subscription, debounceTime, takeUntil } from 'rxjs';
import { GetAllMenuItems$Params } from 'src/api/generated/fn/menu-v-2/get-all-menu-items';
import { GetMenuCategoriesForVenue$Params } from 'src/api/generated/fn/menu-v-2/get-menu-categories-for-venue';
import { RemoveMenuCategory$Params } from 'src/api/generated/fn/menu-v-2/remove-menu-category';
import { RemoveMenuItem$Params } from 'src/api/generated/fn/menu-v-2/remove-menu-item';
import { RemoveMenuSubcategory$Params } from 'src/api/generated/fn/menu-v-2/remove-menu-subcategory';
import { MenuCategoryDto } from 'src/api/generated/models/Digiwork/MaitreD/Pos/DTO/MenuCategories/menu-category-dto';
import { MenuItemDto } from 'src/api/generated/models/Digiwork/MaitreD/Pos/DTO/MenuItems/menu-item-dto';
import { MenuSubcategoryDto } from 'src/api/generated/models/Digiwork/MaitreD/Pos/DTO/MenuSubcategories/menu-subcategory-dto';
import { MenuV2Service } from 'src/api/generated/services';
import { AddOrEditMenuCategoryComponent } from './add-or-edit-menu-category/add-or-edit-menu-category.component';
import { AddOrEditMenuSubcategoryComponent } from './add-or-edit-menu-subcategory/add-or-edit-menu-subcategory.component';
import { CurrentVenueHelperService } from 'src/app/share-modul/services/current-venue-helper/current-venue.helper.service';
import { MenuCurrencyDto } from 'src/api/generated/models/Digiwork/MaitreD/Pos/DTO/Menu/menu-currency-dto';
import { CurrencyService } from 'src/app/pos-panel/services/currency-helper/currency.service';


@Component({
  selector: 'app-menu',
  templateUrl: './menu.component.html',
  styleUrls: ['./menu.component.scss']
})
export class MenuComponent implements OnInit, OnDestroy {

  public unsubscribe: Subject<void> = new Subject<void>();
  public venueId: string;
  public activeIndex: number = 0;
  
  public menuCategories: Array<MenuCategoryDto>;
  public menuItems: Array<MenuItemDto>;
  public venueIdUpdateSubscription: Subscription;

  public searchCategory: string = "";
  public searchMenuItem: string = "";
  searchDebouncerCategory = new Subject<void>();
  searchDebouncerMenuItem = new Subject<void>();
  RELOAD_DEBOUNCE_TIME = 500;

  currency: MenuCurrencyDto;

  loadingCategories: boolean = false;
  loadingMenuItems: boolean = false;

  constructor(
    private route: ActivatedRoute,
    public router: Router,
    public dialogService: DialogService,
    private confirmationService: ConfirmationService,
    public currentVenueHelper: CurrentVenueHelperService,
    public currencyService: CurrencyService,
    public translate: TranslateService,
    private menuV2Service: MenuV2Service
  ) {

    this.route.params.subscribe(params => {
      this.venueId = params['venue-id'];
      this.refreshData();
      this.currentVenueHelper.updateCurrentVenueIdDropdown(params['venue-id']);
    });
    this.route.queryParams.subscribe(params => {
      if (params['activeIndex']) {
        this.activeIndex = params['activeIndex'];
        this.refreshData();
      }
    });
    this.currencyService.currencyObservable().subscribe(currency => this.currency = currency);
    
  }

  ngOnInit(): void {
    this.searchDebouncerCategory.pipe(
      debounceTime(this.RELOAD_DEBOUNCE_TIME)
    ).subscribe(_ => this.getMenuCategoriesForVenue());

    this.searchDebouncerMenuItem.pipe(
      debounceTime(this.RELOAD_DEBOUNCE_TIME)
    ).subscribe(_ => this.getMenuItemsForVenue());
  }

  changeTab(e: any) {
    this.activeIndex = e.index;
    this.router.navigate([],
      {
        relativeTo: this.route,
        queryParams: { activeIndex: this.activeIndex },
        queryParamsHandling: 'merge',
      });
    this.refreshData();
  }
  refreshData() {
    if (this.activeIndex == 0) {
      this.getMenuCategoriesForVenue();
    }
    else if (this.activeIndex == 1) {
      this.getMenuItemsForVenue();
    }
  }
  getMenuCategoriesForVenue() {
    this.loadingCategories = true;
    var params: GetMenuCategoriesForVenue$Params = {} as GetMenuCategoriesForVenue$Params;
    params.venueId = this.venueId;
    params.isRemoved = false;
    params.isMenuSubcategoryRemoved = false;
    params.searchTerm = this.searchCategory;
    this.menuV2Service.getMenuCategoriesForVenue(params)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe({
        next: (response) => {
          this.menuCategories = response.menuCategories ? response.menuCategories : [];
          this.loadingCategories = false;
        },
        error: errorResponse => {
        }
      });
  }
  newCategory() {
    const ref = this.dialogService.open(AddOrEditMenuCategoryComponent, {
      width: '372px',
      showHeader: false,
      closeOnEscape: false,
      data: {
        venueId: this.venueId
      }
    });
    ref.onClose.subscribe((item: any) => {
      this.refreshData();
    });
  }
  editCategory(category: MenuCategoryDto) {
    const ref = this.dialogService.open(AddOrEditMenuCategoryComponent, {
      width: '372px',
      showHeader: false,
      closeOnEscape: false,
      data: {
        venueId: this.venueId,
        item: category
      }
    });
    ref.onClose.subscribe((item: any) => {
      this.refreshData();
    });
  }
  deleteCategory(category: MenuCategoryDto) {
    this.confirmationService.confirm({
      header: this.translate.instant('MENU.DELETE_CATEGORY_TITLE') + "?",
      message: this.translate.instant('MENU.DELETE_CATEGORY_TEXT') + category.name + " ?",
      acceptLabel: this.translate.instant('YES'),
      rejectLabel: this.translate.instant('NO'),
      defaultFocus: "reject",
      closeOnEscape: false,
      acceptButtonStyleClass: 'soc-confirmation-dialog dialog-btn destructive-btn',
      rejectButtonStyleClass: 'soc-confirmation-dialog dialog-btn reject-btn',
      accept: () => {
        var params: RemoveMenuCategory$Params = {} as RemoveMenuCategory$Params;
        if (category.id) {
          params.menuCategoryId = category.id;
        }
        this.menuV2Service.removeMenuCategory(params)
          .pipe(takeUntil(this.unsubscribe))
          .subscribe({
            next: (response) => {
              this.refreshData();
            },
            error: errorResponse => {
            }
          });
      }
    });
  }

  searchCategories(){
    this.loadingCategories = true;
    this.searchDebouncerCategory.next();
  }
  newSubCategory(category: MenuCategoryDto) {
    const ref = this.dialogService.open(AddOrEditMenuSubcategoryComponent, {
      width: '372px',
      showHeader: false,
      closeOnEscape: false,
      data: {
        category: category
      }
    });
    ref.onClose.subscribe((item: any) => {
      this.refreshData();
    });
  }
  editSubCategory(category: MenuCategoryDto, subcategory: MenuSubcategoryDto) {
    const ref = this.dialogService.open(AddOrEditMenuSubcategoryComponent, {
      width: '372px',
      showHeader: false,
      closeOnEscape: false,
      data: {
        category: category,
        subcategory: subcategory
      }
    });
    ref.onClose.subscribe((item: any) => {
      this.refreshData();
    });
  }
  deleteSubCategory(category: MenuCategoryDto, subcategory: MenuSubcategoryDto) {
    this.confirmationService.confirm({
      header: this.translate.instant('MENU.DELETE_SUBCATEGORY_TITLE') + "?",
      message: this.translate.instant('MENU.DELETE_SUBCATEGORY_TEXT') + subcategory.name + " ?",
      acceptLabel: this.translate.instant('YES'),
      rejectLabel: this.translate.instant('NO'),
      defaultFocus: "reject",
      closeOnEscape: false,
      acceptButtonStyleClass: 'soc-confirmation-dialog dialog-btn destructive-btn',
      rejectButtonStyleClass: 'soc-confirmation-dialog dialog-btn reject-btn',
      accept: () => {
        var params: RemoveMenuSubcategory$Params = {} as RemoveMenuSubcategory$Params;
        if (category.id) {
          params.menuCategoryId = category.id;
        }
        if (subcategory.id) {
          params.menuSubcategoryId = subcategory.id;
        }
        this.menuV2Service.removeMenuSubcategory(params)
          .pipe(takeUntil(this.unsubscribe))
          .subscribe({
            next: (response) => {
              this.refreshData();
            },
            error: errorResponse => {
            }
          });
      }
    });
  }
  getMenuItemsForVenue() {
    this.loadingMenuItems = true;
    var params: GetAllMenuItems$Params = {} as GetAllMenuItems$Params;
    params.venueId = this.venueId;
    params.isMenuItemRemoved = false;
    params.isMenuCategoryRemoved = false;
    params.searchTerm = this.searchMenuItem;
    this.menuV2Service.getAllMenuItems(params)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe({
        next: (response) => {
          this.menuItems = response.menuItems ? response.menuItems : [];
          this.loadingMenuItems = false;
        },
        error: errorResponse => {
        }
      });
  }
  newMenuItem() {
    this.router.navigate([this.venueId, "menuV2", "new-menu-item"]);
  }
  menuItemDetails(menuItemId: string | undefined) {
    this.router.navigate([this.venueId, "menuV2", "menu-item", menuItemId]);
  }
  editMenuItem(menuItem: MenuItemDto) {
    this.router.navigate([this.venueId, "menuV2", "edit-menu-item", menuItem.id]);
  }
  deleteMenuItem(menuItem: MenuItemDto) {
    this.confirmationService.confirm({
      header: this.translate.instant('MENU_ITEM.DELETE_MENU_ITEM_TITLE') + "?",
      message: this.translate.instant('MENU_ITEM.DELETE_MENU_ITEM_TEXT') + menuItem.name + " ?",
      acceptLabel: this.translate.instant('YES'),
      rejectLabel: this.translate.instant('NO'),
      defaultFocus: "reject",
      closeOnEscape: false,
      acceptButtonStyleClass: 'soc-confirmation-dialog dialog-btn destructive-btn',
      rejectButtonStyleClass: 'soc-confirmation-dialog dialog-btn reject-btn',
      accept: () => {
        var params: RemoveMenuItem$Params = {} as RemoveMenuItem$Params;
        if (menuItem.id) {
          params.menuItemId = menuItem.id;
        }

        this.menuV2Service.removeMenuItem(params)
          .pipe(takeUntil(this.unsubscribe))
          .subscribe({
            next: (response) => {
              this.refreshData();
            },
            error: errorResponse => {
            }
          });
      }
    });
  }
  searchMenuItems(){
    this.loadingMenuItems = true;
    this.searchDebouncerMenuItem.next();
  }
  public ngOnDestroy(): void {
    if (this.venueIdUpdateSubscription) {
      this.venueIdUpdateSubscription.unsubscribe();
    }
  }
}
